import { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import TotalScansChart from '../../components/AnalyticsView/components/TotalScansChart';
import { useLocalStorage } from '../../utils/hooks/useLocalStorage';
import { useAnalyticsView } from '../../utils/hooks/useAnalyticsView';
import '../../utils/BadCodes';
import { useAuth } from '../../utils/Auth';
import { useOrganization } from '../../utils/OrganizationContext';
import QrRef from '../../components/QrComponents/QrRef';
import { AnimatePresence, motion } from 'motion/react';
import { transition, variants } from './variants';
import styles from './Home.module.css';

export default function Home() {
    const { liquidQRs, orgObj, activeOrgId } = useAuth();
    const { orgMetadata } = useOrganization();
    const { analytics: analytics_id, liquid_qrs } = orgObj?.plan || {};

    const [ _, setStoredQrType ] = useLocalStorage('qr_type');

    const navigate = useNavigate();

    // Use analytics hook for 7-day chart data
    const analytics = useAnalyticsView(activeOrgId, liquidQRs);

    // Set to 7-day timeframe for dashboard
    useEffect(() => {
        if (analytics.handleTimeFrameChange) {
            analytics.handleTimeFrameChange('7d');
        }
    }, [analytics.handleTimeFrameChange]);

    // Memoized calculations for performance
    const totalScans = useMemo(() => {
        if (!liquidQRs || liquidQRs.length === 0) return 0;
        return liquidQRs.reduce((total, qr) => {
            return total + (qr.data?.scanCount_cache || 0);
        }, 0);
    }, [liquidQRs]);

    const topQRs = useMemo(() => {
        if (!liquidQRs || liquidQRs.length === 0) return [];

        // Filter out temporary QRs and sort by scan count
        const validQRs = liquidQRs
            .filter(qr => !qr.id.startsWith('-') && qr.data?.scanCount_cache > 0)
            .sort((a, b) => (b.data?.scanCount_cache || 0) - (a.data?.scanCount_cache || 0));

        return validQRs.slice(0, 5);
    }, [liquidQRs]);

    const atMaxQRs = useMemo(() => {
        if (liquidQRs && (liquidQRs || []).length < 1) return false;
        const validQRCount = liquidQRs.filter(obj => !obj.id.startsWith('-')).length;
        return validQRCount >= liquid_qrs;
    }, [liquidQRs, liquid_qrs]);

    const isPersonalOrg = orgObj?.metadata?.type === 'personal';
    const isAccountExpired = !isPersonalOrg && orgMetadata?.active === false;

    const totalQRCount = useMemo(() => {
        if (!liquidQRs) return 0;
        return liquidQRs.filter(qr => qr.data.active === true && qr.data.qr_type !== 'liquid_link').length;
    }, [liquidQRs]);

    const formatNumber = (num) => {
        if (num >= 1000000) {
            return (num / 1000000).toFixed(1) + 'M';
        } else if (num >= 1000) {
            return (num / 1000).toFixed(1) + 'K';
        }
        return num.toString();
    };

    const renderTopQRs = useMemo(() => {
        if (analytics_id < 1) {
            return (
                <div className={styles.upgradeContainer}>
                    <i className="fa-solid fa-star" style={{ fontSize: '2rem', color: '#d1d5db', marginBottom: '0.5rem' }}></i>
                    <p style={{ margin: '0', fontSize: '0.875rem', color: '#6b7280', textAlign: 'center' }}>
                        <button className={styles.upgradeLink} onClick={() => navigate('/organizations/settings')} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                            Upgrade
                        </button> to see your Top QRs
                    </p>
                </div>
            );
        }

        if (topQRs.length === 0) {
            return (
                <div className={styles.noQRs}>
                    <i className="fa-solid fa-qrcode" style={{ fontSize: '2rem', color: '#d1d5db', marginBottom: '0.5rem' }}></i>
                    <p style={{ margin: '0', fontSize: '0.875rem', color: '#6b7280', textAlign: 'center' }}>
                        Create QRs to see your top performers!
                    </p>
                </div>
            );
        }

        return (
            <div className={styles.topQRsList}>
                {topQRs.map((qr, index) => <TopQr key={qr.id} qr={qr} index={index} formatNumber={formatNumber} /> )}
            </div>
        );
    },[topQRs, analytics_id]);

    return (
        <div className={styles.wrapper}>
            <AnimatePresence mode='wait'>
                <motion.div 
                    className={styles.container}
                    transition={transition}
                    variants={variants}
                    initial='hide'
                    animate='show'
                    exit='hide'
                >
                    <div className={styles.header}>
                        <h1 className={styles.title}>Dashboard</h1>
                    </div>

                    {/* Stats Cards */}
                    <div className={styles.statsGrid}>
                        <div
                            className={`${styles.statCard} ${(atMaxQRs || isAccountExpired) ? styles.disabled : ''}`}
                            onClick={(atMaxQRs || isAccountExpired) ? () => { } : () => {setStoredQrType(null); navigate('/collection/new');}}
                            title={isAccountExpired ? 'Account expired. Visit Organization Settings to reup your subscription.' : undefined}
                        >
                            <div className={styles.statIcon}>
                                <i className="fa-solid fa-plus"></i>
                            </div>
                            <div className={styles.statContent}>
                                <div className={styles.statTitle}>Create QR</div>
                                <div className={styles.statDescription}>
                                    {isAccountExpired ? 'Account expired' : (atMaxQRs ? 'Plan limit reached' : 'Add New QR Code')}
                                </div>
                            </div>
                        </div>

                        <div className={styles.statCard} onClick={() => navigate('/collection')}>
                            <div className={styles.statIcon}>
                                <i className="fa-kit fa-liquidqr"></i>
                            </div>
                            <div className={styles.statContent}>
                                <div className={styles.statValue}>{formatNumber(totalQRCount)}</div>
                                <div className={styles.statTitle}>Your QRs</div>
                            </div>
                        </div>

                        {analytics_id > 0 ? (
                            <div
                                className={styles.statCard}
                                onClick={() => navigate('/analytics')}
                            >
                                <div className={styles.statIcon}>
                                    <i className="fa-solid fa-chart-line"></i>
                                </div>
                                <div className={styles.statContent}>
                                    <div className={styles.statValue}>
                                        {formatNumber(totalScans)}
                                    </div>
                                    <div className={styles.statTitle}>
                                        <span className={styles.statSubtitle}>
                                            Total Scans
                                        </span>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className={styles.statCard} onClick={() => window.location.assign(process.env.REACT_APP_UPGRADE_URL)}>
                                <div className={styles.statIcon}>
                                    <i className="fa-solid fa-chart-line"></i>
                                </div>
                                <div className={styles.statContent}>
                                    <div className={styles.statTitle}>Upgrade</div>
                                    <div className={styles.statDescription}>To View Analytics</div>
                                </div>
                            </div>
                        )}

                        <div className={styles.statCard} onClick={() => window.open('https://liquidqr.com/store', '_blank')}>
                            <div className={styles.statIcon}>
                                <i className="fa-solid fa-shopping-bag"></i>
                            </div>
                            <div className={styles.statContent}>
                                <div className={styles.statTitle}>Order Stickers</div>
                                <div className={styles.statDescription}>Physical QR stickers</div>
                            </div>
                        </div>
                    </div>

                    {/* Main Content */}
                    <div className={styles.mainContent}>
                        {/* Chart Section */}
                        <div className={styles.chartSection}>
                            {analytics_id > 0 ? (
                                analytics.processedData ? (
                                    <TotalScansChart
                                        data={analytics.processedData.timeSeriesData}
                                        title="Total Scans This Week"
                                    />
                                ) : analytics.loading ? (
                                    <div className={styles.chartPlaceholder}>
                                        <div className={styles.chartHeader}>
                                            <h3 className={styles.chartTitle}>Total Scans This Week</h3>
                                        </div>
                                        <div className={styles.upgradePrompt}>
                                            <i className="fa-solid fa-spinner fa-spin" style={{ fontSize: '3rem', color: '#6b7280', marginBottom: '1rem' }}></i>
                                            <p style={{ margin: '0', fontSize: '1.125rem', color: '#6b7280', textAlign: 'center' }}>
                                                Loading analytics data...
                                            </p>
                                        </div>
                                    </div>
                                ) : (
                                    <div className={styles.chartPlaceholder}>
                                        <div className={styles.chartHeader}>
                                            <h3 className={styles.chartTitle}>Total Scans This Week</h3>
                                        </div>
                                        <div className={styles.upgradePrompt}>
                                            <i className="fa-solid fa-chart-line" style={{ fontSize: '3rem', color: '#d1d5db', marginBottom: '1rem' }}></i>
                                            <p style={{ margin: '0', fontSize: '1.125rem', color: '#6b7280', textAlign: 'center' }}>
                                                {liquidQRs?.length === 0 ?
                                                    'Create some QR codes to see your analytics!' :
                                                    'No scan data available for the selected period.'
                                                }
                                            </p>
                                        </div>
                                    </div>
                                )
                            ) : (
                                <div className={styles.chartPlaceholder}>
                                    <div className={styles.chartHeader}>
                                        <h3 className={styles.chartTitle}>Total Scans This Week</h3>
                                    </div>
                                    <div className={styles.upgradePrompt}>
                                        <i className="fa-solid fa-chart-line" style={{ fontSize: '3rem', color: '#d1d5db', marginBottom: '1rem' }}></i>
                                        <p style={{ margin: '0', fontSize: '1.125rem', color: '#6b7280', textAlign: 'center' }}>
                                            <button className={styles.upgradeLink} onClick={() => navigate('/organizations/settings')} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                                                Upgrade your plan
                                            </button> to view detailed analytics
                                        </p>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Top QRs Section */}
                        <div className={styles.topQRsSection}>
                            <div className={styles.sectionHeader}>
                                <h3 className={styles.sectionTitle}>Top QR Codes</h3>
                                <span className={styles.sectionSubtitle}>by total scans</span>
                            </div>
                            {renderTopQRs}
                        </div>
                    </div>
                </motion.div>
            </AnimatePresence>
        </div>
    );
};

const TopQr = ({qr, index, formatNumber}) => {
    const childQrOptions = useMemo(() => qr?.data?.qr_style, [qr?.data?.qr_style]);

    return (
        <div className={styles.topQRItem}>
            <div className={styles.qrRank}>#{index + 1}</div>
            <div className={styles.qrPreview}>
                <QrRef size={71} childQrOptions={childQrOptions} />
            </div>
            <div className={styles.qrInfo}>
                <div className={styles.qrName}>
                    {qr?.data?.nickname || 'Unnamed QR'}
                </div>
                <div className={styles.qrScans}>
                    {formatNumber(qr?.data?.scanCount_cache || 0)} scans
                </div>
            </div>
        </div>
    );
}